/**
 * Escribir un email al cliente desde su ficha. Sale por el correo saliente del bufete (Ajustes →
 * Correo saliente) con «Responder a» el usuario que lo envía, y queda en la línea de tiempo del
 * cliente como comunicación saliente. Solo se puede escribir al cliente o a sus contactos.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Send } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import Modal from '../ui/Modal'
import { inputCls } from './common'
import {
  findPlaceholders,
  type CaseListResponse,
  type ClientEmailOptions,
  type DocumentListItem,
  type EmailTemplateDto,
  type EmailTemplateListResponse,
  type RenderedEmailTemplate,
} from '@toga/shared'

interface Props {
  clientId: string
  onClose: () => void
  /** Se llama tras enviar; `registered` es false si el correo salió pero no quedó en la línea de tiempo. */
  onSent: (registered: boolean) => void
}

const MAX_CC = 5
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/

/** «a@x.es, b@y.es» → ['a@x.es', 'b@y.es'] */
function splitEmails(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

export default function SendEmailModal({ clientId, onClose, onSent }: Props) {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'

  const [opts, setOpts] = useState<ClientEmailOptions | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [cases, setCases] = useState<CaseListResponse['items']>([])
  const [docs, setDocs] = useState<DocumentListItem[]>([])
  const [templates, setTemplates] = useState<EmailTemplateDto[]>([])
  const [templateId, setTemplateId] = useState('')
  /** Lo que escribió la plantilla la última vez: si no se ha tocado, se puede volver a rellenar. */
  const [applied, setApplied] = useState<{ subject: string; body: string } | null>(null)

  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [caseId, setCaseId] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<ClientEmailOptions>(`/clients/${clientId}/email-options`)
      .then((o) => {
        setOpts(o)
        setTo(o.recipients[0]?.email ?? '')
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'No se pudo cargar'))
    api
      .get<CaseListResponse>(`/cases?clientId=${clientId}&pageSize=100`)
      .then((r) => setCases(r.items))
      .catch(() => setCases([]))
    api
      .get<{ items: DocumentListItem[] }>(`/documents?clientId=${clientId}&pageSize=100`)
      .then((r) => setDocs(r.items))
      .catch(() => setDocs([]))
    api
      .get<EmailTemplateListResponse>('/email-templates')
      .then((r) => setTemplates(r.items))
      .catch(() => setTemplates([]))
  }, [clientId])

  // Adjuntables: documentos del cliente sin expediente o del expediente elegido.
  const attachable = useMemo(
    () => docs.filter((d) => !d.caseId || d.caseId === (caseId || null)),
    [docs, caseId]
  )

  // Si se cambia de expediente, se quitan los adjuntos que ya no encajan.
  useEffect(() => {
    setPicked((p) => {
      const ok = new Set(attachable.map((d) => d.id))
      const next = new Set([...p].filter((id) => ok.has(id)))
      return next.size === p.size ? p : next
    })
  }, [attachable])

  const untouched = !!applied && applied.subject === subject && applied.body === body

  /** Rellena asunto y mensaje con la plantilla para este cliente (y el expediente elegido). */
  const renderTemplate = async (id: string, forCase: string) => {
    const r = await api.post<RenderedEmailTemplate>(
      `/clients/${clientId}/email-templates/${id}/render`,
      { caseId: forCase || null }
    )
    setSubject(r.subject)
    setBody(r.body)
    setApplied({ subject: r.subject, body: r.body })
  }

  const pickTemplate = async (id: string) => {
    setError(null)
    if (!id) {
      setTemplateId('')
      setApplied(null)
      return
    }
    const hasText = subject.trim() !== '' || body.trim() !== ''
    if (hasText && !untouched && !confirm('Se reemplazará lo que has escrito. ¿Continuar?')) return
    setTemplateId(id)
    try {
      await renderTemplate(id, caseId)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? 'No se pudo aplicar la plantilla')
          : 'Error de red'
      )
    }
  }

  // Al cambiar de expediente, una plantilla sin tocar se vuelve a rellenar con sus datos.
  const pickCase = async (id: string) => {
    setCaseId(id)
    if (templateId && untouched) {
      try {
        await renderTemplate(templateId, id)
      } catch {
        /* se mantiene el texto anterior */
      }
    }
  }

  // Variables {{…}} que siguen sin rellenar: no se envía hasta resolverlas.
  const pending = findPlaceholders(`${subject}\n${body}`)

  const ccList = splitEmails(cc)
  const ccInvalid = ccList.find((c) => !EMAIL_RE.test(c))
  const ccError = ccInvalid
    ? `«${ccInvalid}» no parece un email`
    : ccList.length > MAX_CC
      ? `Máximo ${MAX_CC} personas en copia`
      : null

  const canSubmit =
    !!opts?.smtpConfigured &&
    !!to &&
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    pending.length === 0 &&
    !ccError &&
    !busy

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setError(null)
    setBusy(true)
    try {
      const r = await api.post<{ communicationId: string | null; registered: boolean }>(
        `/clients/${clientId}/send-email`,
        {
          to,
          cc: ccList,
          subject: subject.trim(),
          body: body.trim(),
          caseId: caseId || null,
          documentIds: [...picked],
        }
      )
      onSent(r.registered)
    } catch (err) {
      if (err instanceof ApiError) {
        const b = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const first = b?.issues?.fieldErrors
          ? Object.values(b.issues.fieldErrors).flat().find(Boolean)
          : undefined
        setError(first ?? b?.error ?? 'No se pudo enviar')
      } else {
        setError('Error de red')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Enviar email al cliente"
      description="Sale por el correo del bufete y queda registrado en su línea de tiempo."
      maxWidth="max-w-2xl"
    >
      {loadError ? (
        <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
          {loadError}
        </div>
      ) : !opts ? (
        <div className="py-8 grid place-items-center t-muted">
          <Loader2 className="animate-spin" size={18} />
        </div>
      ) : !opts.smtpConfigured ? (
        <div className="space-y-4">
          <div className="text-sm rounded-lg px-3.5 py-3 border border-amber-500/30 bg-amber-500/10 text-amber-200">
            El bufete aún no tiene configurado el correo saliente, así que Toga no puede enviar
            emails.{' '}
            {isAdmin ? (
              <>
                Configúralo en{' '}
                <Link to="/settings/email" className="underline font-medium">
                  Ajustes → Correo saliente
                </Link>
                .
              </>
            ) : (
              'Pide a un administrador que lo configure en Ajustes → Correo saliente.'
            )}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover"
            >
              Cerrar
            </button>
          </div>
        </div>
      ) : opts.recipients.length === 0 ? (
        <div className="space-y-4">
          <div className="text-sm rounded-lg px-3.5 py-3 border border-amber-500/30 bg-amber-500/10 text-amber-200">
            Este cliente no tiene ningún email. Añade uno en su ficha (o en un contacto) para poder
            escribirle desde aquí.
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover"
            >
              Cerrar
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {(templates.length > 0 || cases.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {templates.length > 0 && (
                <label className="text-xs t-muted">
                  Plantilla (opcional)
                  <select
                    value={templateId}
                    onChange={(e) => void pickTemplate(e.target.value)}
                    className={`${inputCls} mt-1`}
                  >
                    <option value="">Escribir desde cero</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {cases.length > 0 && (
                <label className="text-xs t-muted">
                  Expediente (opcional)
                  <select
                    value={caseId}
                    onChange={(e) => void pickCase(e.target.value)}
                    className={`${inputCls} mt-1`}
                  >
                    <option value="">Sin expediente: de todo el cliente</option>
                    {cases.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.code} · {c.title}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs t-muted">
              Para
              <select
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className={`${inputCls} mt-1`}
                required
              >
                {opts.recipients.map((r) => (
                  <option key={r.email} value={r.email}>
                    {r.label} · {r.email}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs t-muted">
              Copia (opcional)
              <input
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                className={`${inputCls} mt-1`}
                placeholder="correo1@…, correo2@…"
                aria-invalid={ccError ? true : undefined}
              />
              {ccError && <span className="text-rose-300 mt-1 block">{ccError}</span>}
            </label>
          </div>

          <p className="text-[11px] t-faint -mt-2">
            Sale desde <span className="t-secondary">{opts.fromEmail}</span>. Si el cliente
            responde, la respuesta llega a <span className="t-secondary">{opts.replyTo}</span>.
          </p>

          <label className="text-xs t-muted block">
            Asunto
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              className={`${inputCls} mt-1`}
              placeholder="Ej.: Documentación pendiente de su expediente"
              required
            />
          </label>

          <div>
            <label className="text-xs t-muted block">
              Mensaje
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                maxLength={10000}
                className={`${inputCls} mt-1`}
                placeholder="Escribe el mensaje…"
                required
              />
            </label>
            <div className="mt-2 rounded-lg border border-dashed border-base px-3 py-2">
              <div className="text-[11px] t-faint mb-1">Se añadirá tu firma:</div>
              <pre className="text-xs t-secondary whitespace-pre-wrap font-sans">
                {opts.signature}
              </pre>
            </div>
          </div>

          {attachable.length > 0 && (
            <fieldset>
              <legend className="text-xs t-muted mb-1">
                Adjuntar documentos (máx. 5 y 15 MB en total)
              </legend>
              <div className="max-h-32 overflow-y-auto rounded-lg border border-base p-2 space-y-1">
                {attachable.map((d) => (
                  <label
                    key={d.id}
                    className="flex items-center gap-2 text-xs t-secondary cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={picked.has(d.id)}
                      disabled={!picked.has(d.id) && picked.size >= 5}
                      onChange={(e) =>
                        setPicked((p) => {
                          const n = new Set(p)
                          if (e.target.checked) n.add(d.id)
                          else n.delete(d.id)
                          return n
                        })
                      }
                    />
                    {d.fileName}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {pending.length > 0 && (
            <div
              role="status"
              className="text-xs rounded-lg px-3 py-2 border border-amber-500/30 bg-amber-500/10 text-amber-200"
            >
              Quedan campos sin rellenar:{' '}
              <span className="font-mono">{pending.map((k) => `{{${k}}}`).join(', ')}</span>.
              {pending.some((k) => k.startsWith('causa.')) && caseId === ''
                ? ' Elige un expediente arriba o escríbelos a mano.'
                : ' Escríbelos a mano para poder enviar.'}
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar email
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
